import * as React from 'react';
import { Box, Button, Drawer as MuiDrawer, useTheme } from '@mui/material';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';

import { AppLogo, NavList } from 'components';
import {
  AUTH_LAYOUT_LEFT_DRAWER_CLOSED_WIDTH,
  AUTH_LAYOUT_LEFT_DRAWER_OPENED_WIDTH,
  AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING,
} from 'consts';
import { useAuthSidebarMenu } from './useAuthSidebarMenu';
import { useActiveLink } from './useActiveLink';

interface AuthSidebarMenuDesktopLayoutProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AuthSidebarMenuDesktopLayout: React.FC<AuthSidebarMenuDesktopLayoutProps> = (props) => {
  const theme = useTheme();
  const { open, setOpen } = props;
  const [navItems] = useAuthSidebarMenu();
  const { activeLink } = useActiveLink();

  const handleResize = () => {
    if (window.innerWidth <= 960) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  };

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Drawer
      variant="permanent"
      open={open}
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          borderRight: 'none',
          paddingRight: AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING,
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
          overflowX: 'hidden',
        }}
      >
        <Box
          sx={{
            paddingX: open ? 5 : 2,
            paddingY: open ? 5 : 2,
            transition: theme.transitions.create(['padding'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <AppLogo mini={!open} />
        </Box>

        <Box
          sx={{
            paddingX: open ? 2 : 0,
            transition: theme.transitions.create(['padding'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <NavList
            navItems={navItems}
            direction="column"
            activeLink={activeLink}
            spacing={open ? 3 : 0}
            navItemStyle={
              open ? { paddingX: 3 } : { borderRadius: 0, paddingTop: 1.5, paddingBottom: 1.5, paddingX: 3 }
            }
          />

          {!open && (
            <Button
              fullWidth
              sx={{
                borderRadius: 0,
                paddingTop: 1.1,
                paddingBottom: 1.1,
                backgroundColor: `${theme.palette.primary.main}10`,
              }}
              onClick={() => setOpen(!open)}
            >
              <ChevronRight sx={{ fontSize: 30 }} />
            </Button>
          )}
        </Box>

        {open && (
          <Button
            fullWidth
            sx={{ borderRadius: 0, backgroundColor: `${theme.palette.primary.main}10`, marginTop: 4 }}
            onClick={() => setOpen(!open)}
          >
            <ChevronLeft sx={{ fontSize: 30 }} />
          </Button>
        )}
      </Box>
    </Drawer>
  );
};

export const AuthUserDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: AUTH_LAYOUT_LEFT_DRAWER_OPENED_WIDTH + AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: AUTH_LAYOUT_LEFT_DRAWER_CLOSED_WIDTH + AUTH_LAYOUT_LEFT_DRAWER_RIGHT_PADDING,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));
